<template>
  <div class="invoice-page">

    <main-navigation />

    <div class="container">

      <!-- ACTION BUTTON -->
      <div class="actions">
        <button @click="downloadPDF()" class="btn-download">
          Download PDF
        </button>
      </div>

      <!-- INVOICE CONTENT -->
      <div ref="invoiceContent" class="invoice-box">

        <!-- HEADER -->
        <div class="header">

          <div class="company">
            <h2>{{ businessName }}</h2>
            <p>{{ businessAddress }}</p>
            <p>Tel: {{ businessPhone }}</p>
          </div>

          <div class="invoice-title">
            <h1>INVOICE</h1>
            <p>{{ invoice?.invoice_name }}</p>
          </div>

        </div>

        <!-- CLIENT INFO -->
        <div class="client-info">
          <p><strong>Billed To:</strong> {{ invoice?.client_name }}</p>
          <p><strong>Month:</strong> {{ invoice?.invoice_month }}/{{ invoice?.invoice_year }}</p>
          <p><strong>Date:</strong> {{ today }}</p>
        </div>

        <!-- TABLE -->
        <div class="table-responsive">
        <table class="invoice-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Description</th>
              <th>Amount (KES)</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="bill in sortedBillings" :key="bill.id">
              <td>{{ bill.bill_name }}</td>
              <td>{{ bill.description }}</td>
              <td>{{ formatCurrency(bill.amount) }}</td>
            </tr>
          </tbody>
        </table>
        </div>

        <!-- TOTAL -->
        <div class="total-section">
          <h2>Total: KES {{ formatCurrency(totalAmount) }}</h2>
        </div>

        <!-- PAYMENT SECTION - DYNAMIC BASED ON PROFILE -->
        <div class="payment-section">
          <h3>Payment Instructions</h3>
          
          <!-- Bank Paybill (with hyphen) -->
          <div v-if="isBankPaybill && hasHyphen" class="payment-details bank-paybill">
            <div class="payment-method">
              <strong>Paybill Number:</strong> 
              <span>{{ paybillNumber }}</span>
            </div>
            <div class="payment-method">
              <strong>Account Number:</strong> 
              <span>{{ accountNumber }}#{{ invoice?.Tenant?.tenant_idnumber }}</span>
            </div>
            <div class="payment-note">
              <small>Use your ID number ({{ invoice?.Tenant?.tenant_idnumber }}) as reference</small>
            </div>
          </div>

          <!-- Safaricom Paybill (without hyphen) -->
          <div v-else-if="isSafaricomPaybill" class="payment-details safaricom-paybill">
            <div class="payment-method">
              <strong>Paybill Number:</strong> 
              <span>{{ profile?.BusinessShortCode || 'N/A' }}</span>
            </div>
            <div class="payment-method">
              <strong>Account Number:</strong> 
              <span>{{ invoice?.Tenant?.tenant_idnumber }}</span>
            </div>
          </div>

          <!-- Till Number -->
          <div v-else-if="isTillNumber" class="payment-details till-number">
            <div class="payment-method">
              <strong>Till Number:</strong> 
              <span>{{ profile?.BusinessShortCode || 'N/A' }}</span>
            </div>
            <div class="payment-method">
              <strong>Reference:</strong> 
              <span>{{ invoice?.Tenant?.tenant_idnumber }}</span>
            </div>
          </div>

          <!-- Pochi -->
          <div v-else-if="isPochi" class="payment-details pochi">
            <div class="payment-method">
              <strong>Pochi Number:</strong> 
              <span>{{ profile?.BusinessShortCode || 'N/A' }}</span>
            </div>
            <div class="payment-method">
              <strong>Reference:</strong> 
              <span>{{ invoice?.Tenant?.tenant_idnumber }}</span>
            </div>
          </div>

          <!-- Phone Number -->
          <div v-else-if="isPhoneNumber" class="payment-details phone-number">
            <div class="payment-method">
              <strong>Phone Number:</strong> 
              <span>{{ profile?.BusinessShortCode || 'N/A' }}</span>
            </div>
            <div class="payment-method">
              <strong>Reference:</strong> 
              <span>{{ invoice?.Tenant?.tenant_idnumber }}</span>
            </div>
          </div>

          <!-- Default / Fallback -->
          <div v-else class="payment-details default">
            <div class="payment-method">
              <strong>Payment Number:</strong> 
              <span>{{ profile?.BusinessShortCode || '4050097' }}</span>
            </div>
            <div class="payment-method">
              <strong>Reference:</strong> 
              <span>{{ invoice?.Tenant?.tenant_idnumber }}</span>
            </div>
          </div>
        </div>

        <!-- FOOTER -->
        <div class="footer-tagline">
          <p>
            Powered by Rental Kenya Services, Pacific Crest Building, Mombasa Road<br/>
            Tel: 0725029795
          </p>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export default {
  data() {
    return {
      invoiceId: null,
      invoice: null,
      billings: [],
      profile: null, // Store landlord profile
      loadingProfile: false
    };
  },

  async mounted() {
    this.invoiceId = this.$route.params.invoice_id;

    await this.getInvoice();
    await this.getBillings();
    await this.getLandlordProfile(); // Fetch profile for payment details
  },

  computed: {

    today() {
      return new Date().toLocaleDateString();
    },

    totalAmount() {
      return this.billings.reduce((sum, b) => {
        return sum + Number(b.amount);
      }, 0);
    },

    sortedBillings() {
      const priority = {
        "Rent": 1,
        "Service Charge": 2,
        "Electricity Bill": 3,
        "Water Bill": 4,
      };

      return [...this.billings].sort((a, b) => {
        return (priority[a.bill_name] || 99) - (priority[b.bill_name] || 99);
      });
    },

    // BUSINESS FALLBACKS
    businessName() {
      return this.profile?.Business_name || "Rental Kenya";
    },

    businessAddress() {
      return this.profile?.Business_address || "P.O BOX 103075 - 00101 Nairobi";
    },

    businessPhone() {
      return this.invoice?.Business_phone || "0725029795";
    },

    // Payment type checks
    isBankPaybill() {
      return this.profile?.paybill_type === 'Bank Paybill';
    },

    isSafaricomPaybill() {
      return this.profile?.paybill_type === 'Safaricom Paybill';
    },

    isTillNumber() {
      return this.profile?.paybill_type === 'Safaricom Till';
    },

    isPochi() {
      return this.profile?.paybill_type === 'Pochi';
    },

    isPhoneNumber() {
      return this.profile?.paybill_type === 'Phone Number';
    },

    hasHyphen() {
      return this.profile?.BusinessShortCode?.includes('-');
    },

    // For Bank Paybill - split by hyphen
    paybillNumber() {
      if (this.hasHyphen) {
        return this.profile?.BusinessShortCode?.split('-')[0] || 'N/A';
      }
      return this.profile?.BusinessShortCode || 'N/A';
    },

    accountNumber() {
      if (this.hasHyphen) {
        return this.profile?.BusinessShortCode?.split('-')[1] || 'N/A';
      }
      return this.profile?.account || 'N/A';
    }
  },

  methods: {

    async getInvoice() {
      try {
        const res = await apiFetch("/get/invoices");
        const data = res.data || res;

        this.invoice = data.find(
          inv => inv.id == this.invoiceId
        );

      } catch (err) {
        console.error(err);
      }
    },

    async getBillings() {
      try {
        const res = await apiFetch("/get/bills");
        const data = res.data || res;

        this.billings = data.filter(
          b => b.invoice_id == this.invoiceId
        );

      } catch (err) {
        console.error(err);
      }
    },

    async getLandlordProfile() {
      try {
        this.loadingProfile = true;
        const response = await apiFetch("/profile");
        this.profile = response.data || response;
        console.log("Landlord profile loaded:", this.profile);
      } catch (err) {
        console.error("Failed to load landlord profile:", err);
      } finally {
        this.loadingProfile = false;
      }
    },

    formatCurrency(value) {
      return Number(value).toLocaleString();
    },

    downloadPDF() {
      const element = this.$refs.invoiceContent;

      html2canvas(element, { scale: 2 }).then(canvas => {
        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF("p", "mm", "a4");

        const imgWidth = 210;
        const pageHeight = 295;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save(`${this.invoice.invoice_name}.pdf`);
      });
    }

  }
};
</script>

<style scoped>

.invoice-page {
  padding: 20px;
  background: var(--dash-bg);
}

.container {
  max-width: 900px;
  margin: auto;
}

.actions {
  text-align: right;
  margin-bottom: 15px;
}

.btn-download {
  background: var(--dash-primary);
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: var(--dash-pill);
  cursor: pointer;
}

.btn-download:hover {
  background: var(--dash-primary-hover);
}

/* INVOICE BOX */
.invoice-box {
  background: white;
  padding: 30px;
  border-radius: var(--dash-radius-sm);
}

/* HEADER */
.header {
  display: flex;
  justify-content: space-between;
  border-bottom: 2px solid var(--dash-line);
  padding-bottom: 10px;
  margin-bottom: 20px;
}

.company h2 {
  margin: 0;
}

.invoice-title {
  text-align: right;
}

.invoice-title h1 {
  margin: 0;
  color: var(--dash-success);
}

/* CLIENT */
.client-info {
  margin-bottom: 20px;
}

/* TABLE */
.invoice-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.invoice-table th {
  background: var(--dash-bg-alt);
  text-align: left;
  padding: 10px;
}

.invoice-table td {
  padding: 10px;
  border-bottom: 1px solid var(--dash-line);
}

/* TOTAL */
.total-section {
  text-align: right;
  margin-top: 20px;
  margin-bottom: 30px;
}

/* PAYMENT SECTION */
.payment-section {
  margin-top: 30px;
  padding: 20px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius-xs);
  border-left: 4px solid var(--dash-success);
}

.payment-section h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: var(--dash-ink);
  font-size: 18px;
}

.payment-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.payment-method {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed var(--dash-line-strong);
}

.payment-method:last-child {
  border-bottom: none;
}

.payment-method strong {
  min-width: 140px;
  color: var(--dash-text);
  font-size: 14px;
}

.payment-method span {
  color: var(--dash-ink);
  font-weight: 600;
  font-size: 15px;
  font-family: var(--dash-font-mono);
}

.payment-note {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--dash-line-strong);
}

.payment-note small {
  color: var(--dash-muted);
  font-size: 12px;
}

/* Specific styling for different payment types */
.bank-paybill .payment-method strong {
  color: var(--dash-primary-ink);
}

.safaricom-paybill .payment-method strong {
  color: var(--dash-success);
}

.till-number .payment-method strong {
  color: var(--dash-warning);
}

.pochi .payment-method strong {
  color: var(--dash-primary);
}

.phone-number .payment-method strong {
  color: var(--dash-alt);
}

/* FOOTER */
.footer-tagline {
  margin-top: 40px;
  text-align: center;
  font-size: 12px;
  color: var(--dash-muted);
  border-top: 1px solid var(--dash-line);
  padding-top: 10px;
}

</style>